import { type ResponseData, useQuery, useTeam } from '@vrplatform/data';
import { Icon } from '@vrplatform/icons';
import {
  EllipsisMenuCopyItem,
  EllipsisMenuDivider,
  EllipsisMenuItem,
} from '@vrplatform/components-legacy';
import { Box, Tooltip } from '@mantine/core';
import { HiddenFeatureIndicator } from '../../components';
import { EllipsisMenuSourceDownloadItem } from '../../components/EllipsisMenuSourceDownloadItem';
import { useJournalEntriesModal } from '../../modals';

type BankRecord = ResponseData<'get:/bank-records/{id}'>;
type BankRecordMenuItemRecord = Pick<
  BankRecord,
  'id' | 'status' | 'connectionId' | 'uniqueRef'
> & {
  reconciliation?: {
    transactions?: unknown[] | null;
  } | null;
};

type Props = {
  bankRecord: BankRecordMenuItemRecord;
  openStatusModal: () => void;
  openUnmatchModal: () => void;
};

export const BankRecordEllipsisMenuItems = ({
  bankRecord,
  openStatusModal,
  openUnmatchModal,
}: Props) => {
  const isReconciled = !!bankRecord.reconciliation?.transactions?.[0];

  const disabledUnmatch = bankRecord.status === 'active' && isReconciled;

  return (
    <>
      {isReconciled && (
        <EllipsisMenuItem
          onClick={openUnmatchModal}
          customIcon={<Icon icon={'CrossCircleIcon'} size={16} />}
        >
          Unmatch
        </EllipsisMenuItem>
      )}

      <Tooltip
        label={'Please unmatch the bank record before excluding'}
        withArrow
        disabled={!disabledUnmatch}
      >
        <Box>
          <EllipsisMenuItem
            onClick={openStatusModal}
            disabled={disabledUnmatch}
            customIcon={
              <Icon
                icon={
                  bankRecord.status === 'active' ? 'EyeSlashIcon' : 'EyeIcon'
                }
                size={16}
              />
            }
          >
            {bankRecord.status === 'active' ? 'Exclude' : 'Include'}
          </EllipsisMenuItem>
        </Box>
      </Tooltip>

      <HiddenFeatureIndicator permission="vrp-admin">
        <EllipsisMenuDivider />

        <EllipsisMenuCopyItem value={bankRecord.id} />

        {!!bankRecord.connectionId && (
          <EllipsisMenuSourceDownloadItem
            fileName={bankRecord.uniqueRef || bankRecord.id}
            where={{
              bankRecord: {
                id: {
                  _eq: bankRecord.id,
                },
              },
            }}
          />
        )}
      </HiddenFeatureIndicator>
    </>
  );
};

export const BankBalanceEllipsisMenuItems = ({
  accountId,
  openEditModal,
}: {
  accountId: string;
  openEditModal: () => void;
}) => {
  const { open: openJournalEntriesModal } = useJournalEntriesModal();

  const [{ id: teamId }] = useTeam();

  const { data: transactionId, isLoading: loading } = useQuery(
    (q, args) =>
      args.accountId && args.teamId
        ? q
            .transactions({
              where: {
                uniqueRef: {
                  _eq: `openingBalance_${args.accountId}`,
                },
                tenantId: {
                  _eq: args.teamId,
                },
                lines: {
                  accountAssignmentType: { _eq: 'openingBalance' },
                },
              },
              limit: 1,
            })
            .map((t) => t.id)[0] || null
        : null,
    {
      skip: !accountId || !teamId,
      keepPreviousData: true,
      queryKey: ['bankRecords'],
      variables: {
        teamId,
        accountId,
      },
    }
  );

  return (
    <>
      <EllipsisMenuItem
        customIcon={<Icon icon="Edit3Icon" size={16} />}
        onClick={openEditModal}
      >
        Edit balance
      </EllipsisMenuItem>

      <EllipsisMenuItem
        loading={loading}
        disabled={!transactionId}
        onClick={() =>
          openJournalEntriesModal({
            transactionIds: [transactionId!],
          })
        }
        customIcon={<Icon icon="ListUnorderedIcon" size={16} />}
      >
        Journal Entries
      </EllipsisMenuItem>
    </>
  );
};
